import type {
  Bono,
  ComparisonRow,
  CostRow,
  Faq,
  PriceTier,
  Purchase,
  Stat,
  Testimonial,
  Tomo,
} from "../types/megapack";

/* ============================================================
   CONFIGURACIÓN COMERCIAL
   ============================================================ */
export const offerConfig = {
  /** Fecha/hora de cierre del precio de lanzamiento (hora Argentina) */
  endsAt: "2026-10-19T23:59:00-03:00" as string | null,
  /** Aumento previsto al cerrar el lanzamiento. null = se oculta */
  priceIncrease: "$10.000 ARS" as string | null,
  /** Cupos al precio de lanzamiento. null = se oculta la barra */
  slots: { total: 100, taken: 72 } as { total: number; taken: number } | null,
  checkoutStandard:
    "https://megapack-lavarropas-fallas-y-soluciones.impultienda.ar/checkout?p=976f4c02-6116-488c-bb82-73a12a2e114a",
  checkoutPro:
    "https://megapack-lavarropas-fallas-y-soluciones.impultienda.ar/checkout?p=7b78e7d1-a77d-4178-a647-557abc235a42",
  guaranteeDays: 7,
  paymentMethods: ["Mercado Pago"],
  supportEmail: "[correo de soporte]",
  social: "[@redes]",
  domain: "[dominio]",
};

export const mockupImage =
  "https://cdn.magicpatterns.com/uploads/3rKLybRDMCsP4kvJBS5aPR/Megapack_Lavarropas__Guia_Completa.png";

export const heroChips = ["📄 100% digital", "⚡ Acceso online", "🧺 Por síntomas", "🛠️ Paso a paso"];

export const stats: Stat[] = [
  { value: "6", label: "Tomos completos" },
  { value: "5", label: "Bonos incluidos" },
  { value: "100%", label: "Formato digital" },
  { value: "24/7", label: "Para consultar ⭐" },
];

/* Reseñas de ejemplo — reemplazar por opiniones reales antes de publicar */
export const initialTestimonials: Testimonial[] = [
  {
    initials: "MG",
    name: "Mariana G.",
    location: "Córdoba",
    quote:
      "Dejó de centrifugar y en 15 minutos encontré el síntoma en el Tomo 4. Era la carga desbalanceada.",
  },
  {
    initials: "LP",
    name: "Lucas P.",
    location: "Rosario",
    quote: "Todo explicado sin vueltas. Lo uso como manual cada vez que algo suena raro.",
  },
  {
    initials: "RS",
    name: "Rocío S.",
    location: "Buenos Aires",
    quote:
      "Con la ficha de diagnóstico le expliqué al técnico qué pasaba. El presupuesto fue mucho más claro.",
  },
];

export const moreTestimonials: Testimonial[] = [
  {
    initials: "CF",
    name: "Carlos F.",
    location: "Mendoza",
    version: "Megapack PRO",
    quote: "Tenía un código de error que no entendía. Con la sección por marca supe qué revisar primero.",
  },
  {
    initials: "VR",
    name: "Valeria R.",
    location: "La Plata",
    version: "Megapack",
    quote: "No desagotaba. Seguí el árbol de decisión y encontré el filtro tapado. Lo limpié yo misma.",
  },
  {
    initials: "DM",
    name: "Diego M.",
    location: "Neuquén",
    version: "Megapack",
    quote: "Lo compré por una falla y terminé usando el calendario de mantenimiento todos los meses.",
  },
  {
    initials: "AB",
    name: "Andrea B.",
    location: "Mar del Plata",
    version: "Megapack PRO",
    quote: "Claro, ordenado y sin tecnicismos. Vale cada peso.",
  },
];

export const problemList = [
  "Cada falla te obliga a buscar desde cero.",
  "Mil videos distintos y ninguno es tu modelo.",
  "No sabés qué podés revisar sin riesgo.",
  "Pagás una visita técnica sin saber qué tiene.",
];

export const categoryTiles = [
  { label: "No enciende", color: "#2563eb" },
  { label: "Desagote", color: "#16a34a" },
  { label: "Centrifugado", color: "#ea580c" },
  { label: "Pérdidas", color: "#9333ea" },
  { label: "Ruidos", color: "#475569" },
  { label: "Códigos", color: "#ef4444" },
];

export const tomos: Tomo[] = [
  { number: 1, emoji: "📘", label: "Método de diagnóstico", description: "Del síntoma a la sección correcta." },
  { number: 2, emoji: "📗", label: "No enciende o no arranca", description: "Inicio, cortes de ciclo y revisiones externas." },
  { number: 3, emoji: "📙", label: "Problemas con el agua", description: "No carga, no desagota o pierde agua." },
  { number: 4, emoji: "📕", label: "Centrifugado y ruidos", description: "Carga, nivelación y vibraciones." },
  { number: 5, emoji: "📓", label: "Lavado, residuos y olores", description: "Uso, limpieza y mantenimiento." },
  { number: 6, emoji: "📔", label: "Códigos de error", description: "Qué significan y cuándo llamar al técnico." },
];

export const tomoIncludes = [
  "Organizado por síntomas",
  "Lenguaje simple",
  "Paso a paso",
  "Cuándo frenar y llamar al técnico",
];

export const bonos: Bono[] = [
  { number: 1, title: "Buscador visual de fallas", description: "Del síntoma al tomo exacto, con árboles de decisión imprimibles.", value: 15000 },
  { number: 2, title: "Checklist de seguridad", description: "Qué revisar primero y cuándo detenerse.", value: 12000 },
  { number: 3, title: "Ficha de diagnóstico", description: "Registrá marca, modelo, síntomas y códigos.", value: 13000 },
  { number: 4, title: "Calendario de mantenimiento", description: "Cuidados mes a mes para tu equipo.", value: 14000 },
  { number: 5, title: "Guía de presupuestos", description: "Qué preguntarle al técnico antes de aceptar.", value: 15000 },
];

export const bonusTotalValue = bonos.reduce((sum, b) => sum + b.value, 0);

export const priceTiers: Record<"standard" | "pro", PriceTier> = {
  standard: {
    id: "standard",
    emoji: "📘",
    title: "Megapack Lavarropas",
    subtitle: "6 Tomos + 5 Bonos",
    idealFor: "Ideal para: resolver lo básico sin saber de técnica",
    price: "$19.990 ARS",
    referencePrice: null,
    href: offerConfig.checkoutStandard || "#elegi",
    cta: "→ QUIERO MI MEGAPACK",
  },
  pro: {
    id: "pro",
    emoji: "🏆",
    title: "Megapack Lavarropas PRO ⭐",
    subtitle: "6 Tomos + 5 Bonos + Biblioteca PRO por marca y modelo",
    idealFor: "Ideal para: ir a fondo con códigos de error y fichas por modelo",
    price: "$27.990 ARS",
    referencePrice: null,
    href: offerConfig.checkoutPro || "#elegi",
    cta: "→ QUIERO EL MEGAPACK PRO",
  },
};

export const comparisonRows: ComparisonRow[] = [
  { situation: "Aparece un código de error", without: "No sabés qué significa", withPro: "Lo buscás por marca y modelo." },
  { situation: "Tu modelo tiene una falla típica", without: "Buscás en foros y videos", withPro: "Ficha específica de tu equipo." },
  { situation: "Querés ir más a fondo", without: "Datos sueltos por todos lados", withPro: "Biblioteca ampliada en un solo lugar." },
];

export const proIncludes = [
  "Códigos de error por marca",
  "Fichas por modelo",
  "Diagramas visuales extra",
  "Todo el Megapack incluido",
];

export const faqs: Faq[] = [
  { question: "¿Necesito saber de reparación?", answer: "No. Está pensado para personas sin conocimientos técnicos." },
  { question: "¿Sirve para todas las marcas?", answer: "Cubre los síntomas comunes. Códigos y procedimientos varían según marca y modelo." },
  { question: "¿Cómo recibo el material?", answer: "Lo recibís en formato digital después de la compra." },
  { question: "¿Qué diferencia hay con el PRO?", answer: "El PRO suma la biblioteca por marca y modelo, con códigos de error." },
  { question: "¿Tiene garantía?", answer: "Sí, 7 días según la política de devolución publicada." },
];

export const costRows: CostRow[] = [
  { without: "Buscar en Internet cada vez", withGuide: "El síntoma en 2 minutos" },
  { without: "Explicaciones que se contradicen", withGuide: "Una sola guía clara" },
  { without: "No saber cuándo frenar", withGuide: "Límites de seguridad claros" },
  { without: "Dudas al pedir presupuesto", withGuide: "Preguntas listas para el técnico" },
];

/** Conectar a pedidos confirmados reales */
export const confirmedPurchases: Purchase[] = [];

export const purchasePreview: Purchase = {
  name: "Marcela U.",
  location: "Jujuy",
  product: "Megapack Lavarropas",
  timeAgo: "hace 8 minutos",
};

export const footerLinks = [
  { label: "Términos", href: "#" },
  { label: "Privacidad", href: "#" },
  { label: "Devoluciones", href: "#" },
];
